import { createTheme } from './createTheme';
import { DARK_MODE, GRAY_SCALE } from 'constants/ThemeConstant';
import { rgba, createOpaqueTableRowColors, buildOpaqueTableRowScopedStyles, getTableRowTokens, getColoredHeaderTableTokens, buildThemeTableSorterScopedStyles, buildThemeCheckboxScopedStyles, buildThemeGroupRowCssVariables, buildThemePaginationScopedStyles, getThemePaginationTokens } from './utils';

const NOEL_PRIMARY = '#006E21';
const NOEL_BG = '#FFF7E6';
const NOEL_TEXT = '#531D1D';
const NOEL_BORDER = '#FFD591';
const NOEL_GOLD = rgba('#FFD700', 0.9);
const NOEL_MODULE_ICON = '#FFFFFF';
const NOEL_TABLE_ROW_COLORS = createOpaqueTableRowColors(NOEL_PRIMARY, GRAY_SCALE.WHITE);

/** Sidebar custom-menu — không dùng token Menu global */
export const noelCustomMenuStyles = `
.side-nav .custom-menu .ant-menu-item:not(.ant-menu-item-selected),
.side-nav .custom-menu .ant-menu-submenu:not(.ant-menu-submenu-selected):not(.ant-menu-submenu-open) > .ant-menu-submenu-title {
    color: ${NOEL_TEXT} !important;
}
.side-nav .custom-menu .ant-menu-item:not(.ant-menu-item-selected) .anticon,
.side-nav .custom-menu .ant-menu-item:not(.ant-menu-item-selected) .ant-menu-item-icon,
.side-nav .custom-menu .ant-menu-submenu:not(.ant-menu-submenu-selected):not(.ant-menu-submenu-open) > .ant-menu-submenu-title .anticon,
.side-nav .custom-menu .ant-menu-submenu:not(.ant-menu-submenu-selected):not(.ant-menu-submenu-open) > .ant-menu-submenu-title .ant-menu-item-icon {
    color: ${NOEL_GOLD} !important;
}
.side-nav .custom-menu .ant-menu-item-group-title,
.side-nav .custom-menu .ant-menu-item-group-title .nav-item_title {
    color: ${NOEL_GOLD} !important;
}
.custom-menu-popup.ant-menu-dark .ant-menu-item:not(.ant-menu-item-selected),
.custom-menu-popup.ant-menu-dark .ant-menu-submenu:not(.ant-menu-submenu-selected):not(.ant-menu-submenu-open):not(.ant-menu-submenu-active) > .ant-menu-submenu-title {
    color: ${GRAY_SCALE.WHITE} !important;
}
.custom-menu-popup.ant-menu-dark .ant-menu-item:not(.ant-menu-item-selected) .anticon,
.custom-menu-popup.ant-menu-dark .ant-menu-submenu:not(.ant-menu-submenu-selected):not(.ant-menu-submenu-open) > .ant-menu-submenu-title .anticon {
    color: ${NOEL_GOLD} !important;
}
`;

/** Steps điều hướng form (className="step-nav") */
export const noelStepNavStyles = `
.step-nav.ant-steps .ant-steps-item-title {
    color: ${NOEL_TEXT} !important;
    font-size: 13px !important;
}
.step-nav.ant-steps .ant-steps-item-process .ant-steps-item-title,
.step-nav.ant-steps .ant-steps-item-finish .ant-steps-item-title {
    color: ${NOEL_PRIMARY} !important;
    font-weight: 600 !important;
}
.step-nav.ant-steps .ant-steps-item-process .ant-steps-item-icon,
.step-nav.ant-steps .ant-steps-item-finish .ant-steps-item-icon {
    background-color: transparent !important;
    border-color: ${NOEL_PRIMARY} !important;
}
.step-nav.ant-steps .ant-steps-item-process .ant-steps-item-icon .ant-steps-icon,
.step-nav.ant-steps .ant-steps-item-process .ant-steps-item-icon .anticon,
.step-nav.ant-steps .ant-steps-item-finish .ant-steps-item-icon .ant-steps-icon,
.step-nav.ant-steps .ant-steps-item-finish .ant-steps-item-icon .anticon {
    color: ${NOEL_PRIMARY} !important;
}
.step-nav.ant-steps .ant-steps-item-wait .ant-steps-item-icon {
    background-color: transparent !important;
    border-color: ${NOEL_BORDER} !important;
}
.step-nav.ant-steps .ant-steps-item-wait .ant-steps-item-icon .ant-steps-icon,
.step-nav.ant-steps .ant-steps-item-wait .ant-steps-item-icon .anticon {
    color: ${GRAY_SCALE.GRAY} !important;
}
`;

export const noelScopedStyles = `
${noelCustomMenuStyles}
${noelStepNavStyles}
${buildThemePaginationScopedStyles(NOEL_GOLD, NOEL_BORDER)}
.ant-table-wrapper .ant-table-thead > tr > th {
    font-weight: 600;
    text-transform: uppercase;
}
${buildThemeTableSorterScopedStyles(NOEL_PRIMARY)}
${buildOpaqueTableRowScopedStyles(NOEL_TABLE_ROW_COLORS)}
${buildThemeCheckboxScopedStyles('light')}
${buildThemeGroupRowCssVariables(NOEL_PRIMARY, NOEL_GOLD, 'light', NOEL_BG)}
`;

export const noelTheme = createTheme({
    primaryColor: NOEL_PRIMARY,
    successColor: '#52C41A',
    warningColor: '#FA8C16',
    errorColor: '#FF4D4F',

    bgColor: NOEL_BG,
    textColor: NOEL_TEXT,
    borderColor: NOEL_BORDER,
    bodyBg: NOEL_BG,
    colorBgLayout: NOEL_BG,
    colorBgElevated: GRAY_SCALE.WHITE,

    menuGroupTitleColor: NOEL_GOLD,
    moduleMenuBg: NOEL_PRIMARY,
    moduleMenuIconColor: NOEL_MODULE_ICON,
    moduleMenuItemSelectedBg: GRAY_SCALE.WHITE,
    moduleMenuItemSelectedColor: NOEL_PRIMARY,
    headerNavColor: NOEL_PRIMARY,
    siderBg: GRAY_SCALE.WHITE,
    customMenuBg: GRAY_SCALE.WHITE,
    customMenuItemSelectedColor: NOEL_TEXT,
    customMenuItemSelectedBg: NOEL_BG,
    customMenuItemSelectedIconColor: NOEL_PRIMARY,
    customMenuItemSelectedBorderColor: NOEL_PRIMARY,

    footerBg: NOEL_PRIMARY,
    footerTextColor: GRAY_SCALE.WHITE,

    componentOverrides: {
        Layout: {
            bodyBg: NOEL_BG,
            headerBg: NOEL_BG,
            siderBg: GRAY_SCALE.WHITE,
        },
        Menu: {
            darkItemBg: 'transparent',
            darkItemColor: GRAY_SCALE.WHITE,
            darkItemSelectedBg: DARK_MODE.ACTIVE_BG_COLOR,
            darkItemSelectedColor: GRAY_SCALE.WHITE,
            darkItemHoverBg: DARK_MODE.HOVER_BG_COLOR,
            darkItemHoverColor: GRAY_SCALE.GRAY_DARK,
            darkSubMenuItemBg: DARK_MODE.BG_COLOR,
            popupBg: DARK_MODE.BG_COLOR,
            activeBarWidth: 0,
            boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
        },
        Breadcrumb: {
            itemColor: GRAY_SCALE.GRAY,
            lastItemColor: NOEL_PRIMARY,
            linkColor: GRAY_SCALE.GRAY,
            linkHoverColor: NOEL_PRIMARY,
            separatorColor: GRAY_SCALE.GRAY,
        },
        Card: {
            colorBgContainer: GRAY_SCALE.WHITE,
            colorTextHeading: NOEL_PRIMARY,
            colorText: NOEL_TEXT,
            colorBorderSecondary: NOEL_BORDER,
            extraColor: NOEL_TEXT,
            headerBg: GRAY_SCALE.WHITE,
            actionsBg: GRAY_SCALE.WHITE,
            borderRadiusLG: 22,
        },
        Table: {
            colorText: NOEL_TEXT,
            colorTextHeading: GRAY_SCALE.WHITE,
            headerBg: NOEL_PRIMARY,
            borderColor: '#F0F0F0',
            ...getColoredHeaderTableTokens(NOEL_PRIMARY),
            ...getTableRowTokens(NOEL_TABLE_ROW_COLORS),
        },
        Steps: {
            colorPrimary: NOEL_PRIMARY,
            colorText: NOEL_TEXT,
            colorTextDescription: GRAY_SCALE.GRAY,
        },
        Input: {
            colorBgContainer: GRAY_SCALE.WHITE,
            colorBorder: NOEL_BORDER,
            activeBorderColor: NOEL_PRIMARY,
            hoverBorderColor: NOEL_PRIMARY,
        },
        DatePicker: {
            colorBgContainer: GRAY_SCALE.WHITE,
            colorBorder: NOEL_BORDER,
            activeBorderColor: NOEL_PRIMARY,
            hoverBorderColor: NOEL_PRIMARY,
        },
        TimePicker: {
            colorBgContainer: GRAY_SCALE.WHITE,
            colorBorder: NOEL_BORDER,
            activeBorderColor: NOEL_PRIMARY,
            hoverBorderColor: NOEL_PRIMARY,
        },
        DateTimePicker: {
            colorBgContainer: GRAY_SCALE.WHITE,
            colorBorder: NOEL_BORDER,
            activeBorderColor: NOEL_PRIMARY,
            hoverBorderColor: NOEL_PRIMARY,
        },
        Checkbox: {
            colorBgContainer: GRAY_SCALE.WHITE,
            colorBorder: NOEL_BORDER,
            activeBorderColor: NOEL_PRIMARY,
            hoverBorderColor: NOEL_PRIMARY,
        },
        Radio: {
            colorBgContainer: GRAY_SCALE.WHITE,
            colorBorder: NOEL_BORDER,
            activeBorderColor: NOEL_PRIMARY,
            hoverBorderColor: NOEL_PRIMARY,
        },
        Select: {
            colorBgContainer: GRAY_SCALE.WHITE,
            selectorBg: GRAY_SCALE.WHITE,
            colorBgElevated: GRAY_SCALE.WHITE,
            optionSelectedBg: rgba(NOEL_PRIMARY, 0.1),
            optionActiveBg: rgba(NOEL_PRIMARY, 0.06),
            colorBorder: NOEL_BORDER,
            activeBorderColor: NOEL_PRIMARY,
            hoverBorderColor: NOEL_PRIMARY,
        },

        Button: {
            defaultBg: GRAY_SCALE.WHITE,
            defaultHoverBg: GRAY_SCALE.WHITE,
            defaultActiveBg: GRAY_SCALE.WHITE,
            defaultBorderColor: NOEL_BORDER,
            defaultHoverBorderColor: NOEL_PRIMARY,
            defaultActiveBorderColor: NOEL_PRIMARY,
        },
        Pagination: getThemePaginationTokens(NOEL_PRIMARY, NOEL_BORDER),
    },
});
